import { AppState } from 'models/AppState'
import { DATA_TABLE_CHANGE_PAGE } from 'components/DataTable/containers/dataTableReducer'
import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import removeUserMutation from 'graphql/mutations/RemoveUser'
import createUserMutation from 'graphql/mutations/CreateUser'
import adminUpdateUserMutation from 'graphql/mutations/AdminUpdateUser'
import AdminUsers from 'components/AdminUsers/AdminUsers'
import { getLocalStorageToken } from 'store/localStorage'

import { useTranslation } from 'react-i18next'
import { useMutation } from "@apollo/client/react";
import toast from 'react-hot-toast'
import { removeUser, removeUserVariables } from 'graphql/mutations/types/removeUser'
import { createUser, createUserVariables } from 'graphql/mutations/types/createUser'
import { adminUpdateUser, adminUpdateUserVariables } from 'graphql/mutations/types/adminUpdateUser'

export interface ICreateUserForm {
  username: string
  password: string
  privilegeLevel: number
}

export const ADMIN_USERS_ID = 'adminTable'
export const ADMIN_USERS_PAGE_SIZE = 5

export const AdminUsersContainer: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const createUserStatus = useSelector((state: AppState) => state.admin.createUserStatus)
  const resetPagination = () =>
    dispatch({
      type: DATA_TABLE_CHANGE_PAGE,
      payload: { id: ADMIN_USERS_ID, pageInfo: { first: 0, after: ADMIN_USERS_PAGE_SIZE } },
    })

  const [remove] = useMutation<removeUser, removeUserVariables>(removeUserMutation, {
    refetchQueries: ['usersList'],
  })
  const [create] = useMutation<createUser, createUserVariables>(createUserMutation, {
    refetchQueries: ['usersList'],
  })
  const [update] = useMutation<adminUpdateUser, adminUpdateUserVariables>(adminUpdateUserMutation, {
    refetchQueries: ['usersList', 'organisations', 'terminalsList'],
    update: (cache) => {
      cache.modify({
        fields: {
          terminalsPaginated(_, { DELETE }) {
            return DELETE
          },
        },
      })
    },
  })

  const removeUser = async (userId: number) => {
    await remove({ variables: { token: getLocalStorageToken(), userId } })
    toast.success((t('AdminUsersContainer.userRemoved') as string) ?? '')
  }
  const createUser = async (
    values: {
      username: string
      password: string
      privilegeLevel: number
    },
    resetValues: () => void,
  ) => {
    let errorMessage: string | null = null
    const { username, password, privilegeLevel } = values
    !username && (errorMessage = t('AdminUsersContainer.emailMissing'))
    !password && (errorMessage = t('AdminUsersContainer.passwordMissing'))
    password.length < 5 && (errorMessage = t('Password.incorrectLength'))
    privilegeLevel === null && (errorMessage = t('AdminUsersContainer.missingPrivilegeLevel'))
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }
    const res = await create({
      variables: {
        token: getLocalStorageToken(),
        username,
        password,
        privilegeLevel,
      },
    })
    resetValues()
    res?.data?.createUser
      ? toast.success((t('AdminUsersContainer.userCreated') as string) ?? '')
      : toast.error((t('AdminUsersContainer.userCreatedFailed') as string) ?? '')
  }
  const updatePrivilegeLevel = async (userId: number, privilegeLevel: number) => {
    await update({
      variables: { token: getLocalStorageToken(), userId, privilegeLevel },
    })
    toast.success((t('AdminUsersContainer.userUpdated') as string) ?? '')
  }
  const updateOrganisationId = async (userId: number, OrganisationId: number[]) => {
    await update({
      variables: {
        token: getLocalStorageToken(),
        userId,
        OrganisationIds: OrganisationId,
      },
    })
    toast.success((t('AdminUsersContainer.userUpdated') as string) ?? '')
  }
  return (
    <AdminUsers
      createUserStatus={createUserStatus}
      resetPagination={resetPagination}
      updatePrivilegeLevel={updatePrivilegeLevel}
      updateOrganisationId={updateOrganisationId}
      createUser={createUser}
      removeUser={removeUser}
    />
  )
}
